import { Fragment, type ReactNode, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Checkbox } from './checkbox.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from './dropdown-menu.tsx'
import { EmptyState, type EmptyStateProps } from './empty-state.tsx'
import { Icon } from './icon.tsx'
import { colorTransition } from './motion.ts'
import { type useRowSelection } from './row-selection.ts'
import { Skeleton } from './skeleton.tsx'

/**
 * The slice of {@link useRowSelection} the Table consumes. The consumer owns the
 * hook (so it can also drive a bulk-action bar) and hands the Table this view of
 * it; passing `selection` switches on the select-all header + per-row checkboxes.
 */
export type RowSelection = Pick<
	ReturnType<typeof useRowSelection>,
	'isSelected' | 'toggle' | 'toggleAll' | 'allSelected' | 'someSelected'
>

/** The select-all header checkbox's tri-state, derived from the selection. */
function headerCheckedState(
	selection: RowSelection,
): boolean | 'indeterminate' {
	if (selection.allSelected) return true
	if (selection.someSelected) return 'indeterminate'
	return false
}

/**
 * The empty-state content shown when a table has no rows — the content fields of
 * the shared {@link EmptyStateProps}. Tables always render the `default` variant,
 * so `variant`/`className` are narrowed out; EmptyState stays the single source
 * of truth for the field shape.
 */
export type TableEmptyState = Pick<
	EmptyStateProps,
	'icon' | 'title' | 'description' | 'action'
>

export type TableColumn<Row> = {
	/** Stable key for the column. */
	key: string
	/** Header cell content. */
	header: ReactNode
	/** Render the body cell for a row. */
	cell: (row: Row) => ReactNode
	/** Optional className for the body cell. */
	className?: string
	/** Optional className for the header cell. */
	headerClassName?: string
}

type TableProps<Row> = {
	/** Column definitions, left-to-right. */
	columns: Array<TableColumn<Row>>
	/** Row data. */
	data: Row[]
	/** Stable id for a row (drives React keys and selection). */
	getRowId: (row: Row) => string
	/**
	 * When set, the whole row becomes a link to this href — its default action.
	 * Rendered as a stretched overlay anchor (keyboard-focusable, supports
	 * cmd/middle-click); the selection checkbox and ⋯ menu sit above it and stay
	 * independently clickable. Pair with {@link getRowLabel} for the link's
	 * accessible name.
	 */
	getRowHref?: (row: Row) => string
	/**
	 * When set, each row becomes a focusable click target (`tabindex="0"`,
	 * Enter/Space activate) firing this handler — for row actions with side
	 * effects (e.g. mark-read-then-navigate), where {@link getRowHref}'s GET-only
	 * link would leak the side effect onto prefetch. Clicks landing on
	 * interactive content inside a row (links, buttons, checkboxes, menu items)
	 * stay with that control. Pair with {@link getRowLabel} for the row's
	 * accessible name. Provide either this or {@link getRowHref}, not both — a
	 * stretched row link would swallow every row click before this handler.
	 */
	onRowClick?: (row: Row) => void
	/**
	 * `grid-template-columns` for the data columns. The selection and row-action
	 * tracks (when enabled) are added automatically around it.
	 */
	columnTemplate: string
	/**
	 * Row-selection state from {@link useRowSelection}. When present, the Table
	 * renders the select-all header checkbox and a per-row checkbox.
	 */
	selection?: RowSelection
	/** Accessible label for a row's selection checkbox (e.g. the row title). */
	getRowLabel?: (row: Row) => string
	/** Per-row overflow-menu content (a fragment of `DropdownMenuItem`s). */
	rowActions?: (row: Row) => ReactNode
	/**
	 * Optional expandable detail rendered in a full-width row directly beneath its
	 * row. Return `null`/`undefined` to leave a row un-expanded (the consumer owns
	 * the open/closed state and the expand control in a cell). When any row has
	 * detail the grid stays the same — the detail row spans every column.
	 */
	renderRowDetail?: (row: Row) => ReactNode
	/** Accessible label for a row's ⋯ action trigger. @default 'Row actions' */
	getRowActionsLabel?: (row: Row) => string
	/**
	 * Branded empty state shown (in place of the header + rows) when `data` is
	 * empty and not loading — a dashed card with an optional brand icon tile.
	 */
	emptyState?: TableEmptyState
	/** Render Skeleton placeholder rows instead of the data. */
	loading?: boolean
	/** Number of Skeleton rows to show while loading. @default 3 */
	loadingRows?: number
	/**
	 * Optional per-row className derived from row data, merged into the data row's
	 * class list *after* the built-in classes (so consumer tokens win under
	 * tailwind-merge). Data rows only — never the skeleton or `renderRowDetail`
	 * rows. Return `undefined` to leave a row untouched. Prop-only extension,
	 * cf. {@link onRowClick} / {@link emptyState}.
	 */
	getRowClassName?: (row: Row) => string | undefined
	/** Tint alternating rows. @default false */
	zebra?: boolean
	/**
	 * Footer slot rendered beneath the rows (e.g. a `<Pagination/>`). A footer
	 * child that renders **nothing** collapses its own chrome via `empty:hidden`,
	 * so a pager can be passed unconditionally — but that only fires when the slot
	 * is truly empty: render `null` (not `''` or an empty wrapper element) to lean
	 * on it. A footer that always renders content (a summary line, a range label)
	 * shows its border regardless.
	 */
	footer?: ReactNode
	className?: string
	'aria-label'?: string
}

function Table<Row>({
	columns,
	data,
	getRowId,
	getRowHref,
	onRowClick,
	columnTemplate,
	selection,
	getRowLabel,
	rowActions,
	getRowActionsLabel,
	renderRowDetail,
	emptyState,
	getRowClassName,
	loading = false,
	loadingRows = 3,
	zebra = false,
	footer,
	className,
	'aria-label': ariaLabel,
}: TableProps<Row>) {
	const { t } = useTranslation()
	// The select-all checkbox occupies a leading `min-content` track and the ⋯
	// action menu a trailing one, around the consumer's data columns.
	const fullTemplate = [
		selection ? 'min-content' : null,
		columnTemplate,
		rowActions ? 'min-content' : null,
	]
		.filter(Boolean)
		.join(' ')
	const rowStyle = { gridTemplateColumns: fullTemplate }
	// Show the branded empty card only when the consumer supplied one; otherwise
	// fall through to the header over zero rows.
	const showEmpty = !loading && data.length === 0 && emptyState != null
	// Count the data columns plus the selection/action tracks, so each skeleton
	// row fills the same grid as a real one.
	const cellCount = columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)

	return (
		<div
			data-slot="table"
			className={cn(
				'bg-card border-border overflow-hidden rounded-lg border',
				className,
			)}
		>
			{showEmpty && emptyState ? (
				// The table's zero-state converges on the shared EmptyState grammar
				// (ringed icon disc), padded to sit inside the card. No `role="table"`
				// here: a table with no rows fails axe `aria-required-children`.
				<EmptyState {...emptyState} />
			) : (
				<>
					{/* `role="table"` wraps only the rowgroups — the footer (pagination)
					    is a sibling so it isn't an invalid table child. */}
					<div role="table" aria-label={ariaLabel} aria-busy={loading || undefined}>
					<div role="rowgroup">
						<div
							role="row"
							style={rowStyle}
							className="text-muted-foreground border-border text-body-2xs tracking-eyebrow grid items-center gap-2.5 border-b bg-[var(--gray-1)] px-3 py-2 font-medium uppercase"
						>
							{selection ? (
								<div role="columnheader">
									<Checkbox
										checked={headerCheckedState(selection)}
										onCheckedChange={() => selection.toggleAll()}
										aria-label="Select all rows"
									/>
								</div>
							) : null}
							{columns.map((column) => (
								<div
									key={column.key}
									role="columnheader"
									className={column.headerClassName}
								>
									{column.header}
								</div>
							))}
							{rowActions ? (
								<div role="columnheader">
									<span className="sr-only">{t('table.actions')}</span>
								</div>
							) : null}
						</div>
					</div>
					<div role="rowgroup">
						{loading
							? Array.from({ length: loadingRows }, (_, rowIndex) => (
									<div
										key={`skeleton-${rowIndex}`}
										role="row"
										aria-hidden="true"
										style={rowStyle}
										className="border-border grid items-center gap-2.5 border-b px-3 py-2 last:border-b-0"
									>
										{Array.from({ length: cellCount }, (_, cellIndex) => (
											<div key={cellIndex} role="cell">
												<Skeleton className="h-4 w-full" />
											</div>
										))}
									</div>
								))
							: data.map((row) => {
									const id = getRowId(row)
									const isSelected = selection?.isSelected(id) ?? false
									const detail = renderRowDetail?.(row)
									const href = getRowHref?.(row)
									// A click landing on interactive content inside the row (a
									// link, button, checkbox, menu item) belongs to that control —
									// the row handler only claims clicks on the static cells.
									const handleRowClick =
										onRowClick == null
											? undefined
											: (event: SyntheticEvent) => {
													const target = event.target as HTMLElement
													if (
														target.closest(
															'a,button,[role="checkbox"],[role="menuitem"]',
														)
													) {
														return
													}
													onRowClick(row)
												}
									return (
										<Fragment key={id}>
											<div
												role="row"
												style={rowStyle}
												tabIndex={handleRowClick == null ? undefined : 0}
												onClick={handleRowClick}
												onKeyDown={
													handleRowClick == null
														? undefined
														: (event) => {
																// Only activate on the row itself — a nested
																// control's keys are its own.
																if (event.target !== event.currentTarget) return
																if (
																	event.key !== 'Enter' &&
																	event.key !== ' '
																) {
																	return
																}
																// Space scrolls the page by default.
																event.preventDefault()
																handleRowClick(event)
															}
												}
												aria-label={
													handleRowClick == null
														? undefined
														: getRowLabel?.(row)
												}
												className={cn(
													'border-border text-body-sm grid items-center gap-2.5 border-b px-3 py-2',
													colorTransition,
													detail == null && 'last:border-b-0',
													zebra && 'even:bg-muted/40',
													// Ramp hover for every non-selected row; onRowClick rows add
													// the press step (full-width surfaces take the colour step,
													// never the discrete press-scale — see motion.ts).
													!isSelected && 'hover:bg-[var(--bg-hover)]',
													handleRowClick != null && 'active:bg-[var(--bg-active)]',
													// Selection tint = brand-alpha formula (rest /.06, hover /.09),
													// over the brand left bar.
													isSelected &&
														'shadow-[inset_3px_0_0_0_var(--brand)] bg-[oklch(from_var(--brand)_l_c_h/0.06)] hover:bg-[oklch(from_var(--brand)_l_c_h/0.09)]',
													href != null && 'group relative cursor-pointer',
													handleRowClick != null && 'focus-cosy cursor-pointer',
													// Consumer classes land last so their tokens win under
													// tailwind-merge; data rows only, never skeleton/detail.
													getRowClassName?.(row),
												)}
											>
												{href != null ? (
													// Stretched overlay anchor: a real link covering the row so
													// the whole row navigates (keyboard + cmd/middle-click), while
													// the static cells below it pass clicks through and the
													// z-raised checkbox / ⋯ menu stay independently clickable.
													// A bare `<a>` under `role="row"` trips axe
													// `aria-required-children` (rows may only own cells), so
													// it rides in a `display:contents` cell — no grid track,
													// the overlay still resolves against the `relative` row.
													<div role="cell" className="contents">
														<Link
															to={href}
															aria-label={getRowLabel?.(row)}
															className="focus-cosy absolute inset-0 rounded-md"
														/>
													</div>
												) : null}
												{selection ? (
													<div role="cell" className="relative z-10 w-fit">
														<Checkbox
															checked={isSelected}
															onCheckedChange={() => selection.toggle(id)}
															aria-label={`Select ${getRowLabel?.(row) ?? 'row'}`}
														/>
													</div>
												) : null}
												{columns.map((column) => (
													<div
														key={column.key}
														role="cell"
														className={column.className}
													>
														{column.cell(row)}
													</div>
												))}
												{rowActions ? (
													<div
														role="cell"
														className="relative z-10 justify-self-end"
													>
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon-sm"
																	aria-label={
																		getRowActionsLabel?.(row) ?? 'Row actions'
																	}
																>
																	<Icon
																		name="dots-horizontal"
																		className="size-4"
																	/>
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																{rowActions(row)}
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												) : null}
											</div>
											{detail != null ? (
												<div
													role="row"
													className="border-border bg-muted/30 border-b px-3 py-2.5 last:border-b-0"
												>
													<div role="cell">{detail}</div>
												</div>
											) : null}
										</Fragment>
									)
								})}
					</div>
					</div>
					{footer != null ? (
						// `empty:hidden` collapses the footer chrome (border + padding) when
						// the footer child renders nothing — e.g. a `<Pagination/>` that
						// returns `null` at a single page — so callers pass it
						// unconditionally without re-checking `pageCount > 1`.
						<div
							data-slot="table-footer"
							className="border-border flex items-center justify-end border-t px-3 py-2 empty:hidden"
						>
							{footer}
						</div>
					) : null}
				</>
			)}
		</div>
	)
}

export { Table }
